// Geometry hygiene for EUDR GeoJSON, per the EU "EUDR GeoJson File Description" v1.5 common-errors list.

export const GeometryWarning = {
  OpenRing: 'openRing',
  HasHoles: 'hasHoles',
  DuplicatePoints: 'duplicatePoints',
  Degenerate: 'degenerate',
  SelfIntersecting: 'selfIntersecting',
  // The EU validator reports MultiPolygon parts that touch — even at a single point — as a
  // self-intersecting polygon, stricter than OGC (which only forbids shared edges/overlap).
  TouchingParts: 'touchingParts',
  // EU error #5: LineString/MultiLineString/GeometryCollection are not accepted production-place shapes.
  InvalidGeometryType: 'invalidGeometryType',
  // EU error #11: positions outside ±90 lat / ±180 lon (a classic cause: swapped longitude/latitude order).
  CoordinateRange: 'coordinateRange',
  // EU error #14: structurally wrong coordinates (non-numeric positions, Point coords as array-of-arrays).
  MalformedCoordinates: 'malformedCoordinates',
  // EU error #6: a ring whose points are collinear — a "shape" with no area.
  ZeroArea: 'zeroArea',
  // EU error #14: Area must be a JSON number; "Area": "3" reads as area 0 in the EU system.
  AreaNotNumeric: 'areaNotNumeric',
  // EU error #12: ProducerCountry must be an ISO2 code (two uppercase letters).
  InvalidProducerCountry: 'invalidProducerCountry',
  // EU error #9: property names are case-sensitive; "productionplace" is silently ignored by the EU system.
  PropertyCase: 'propertyCase',
  // An Osapiens rule, not an EU one: polygons or parts at or below MIN_USABLE_PART_AREA_M2. Non-blocking because
  // the finalize sanitizer drops them from the EU submission file.
  UndersizedArea: 'undersizedArea'
}

// The geometry types the EU accepts for production places (spec §3/§4).
export const ACCEPTED_GEOMETRY_TYPES = ['Point', 'MultiPoint', 'Polygon', 'MultiPolygon']

// The EU-recognized property names (exact case) and our camelCase working equivalents.
const EU_PROPERTY_NAMES = ['ProductionPlace', 'ProducerName', 'ProducerCountry', 'Area']
const ACCEPTED_PROPERTY_SPELLINGS = new Set([
  ...EU_PROPERTY_NAMES,
  'productionPlace', 'producerName', 'producerCountry', 'area'
])

// Per-geometry memo keyed by object identity. Every EUDR edit path assigns a new geometry object rather than
// mutating one (roundGeometryCoordinates only runs on fresh clones or parser output), so an entry never goes stale.
// Without this, a collection-wide analysis reruns O(n²) ring checks on every shape after each edit.
const geometryMemo = new WeakMap()
function memoized (geometry, key, compute) {
  if (!geometry || typeof geometry !== 'object') return compute()
  let memo = geometryMemo.get(geometry)
  if (!memo) {
    memo = {}
    geometryMemo.set(geometry, memo)
  }
  if (!(key in memo)) memo[key] = compute()
  return memo[key]
}

// Rounds coordinates to 6 decimals (~0.1m) to match the function-app agent's precision. Mutates and returns node.
export function roundGeometryCoordinates (node) {
  if (Array.isArray(node)) {
    for (let i = 0; i < node.length; i++) {
      if (typeof node[i] === 'number') node[i] = Number(node[i].toFixed(6))
      else roundGeometryCoordinates(node[i])
    }
  } else if (node && typeof node === 'object') {
    for (const key of Object.keys(node)) roundGeometryCoordinates(node[key])
  }
  return node
}

function samePoint (a, b) {
  return a[0] === b[0] && a[1] === b[1]
}

export function ringIsClosed (ring) {
  return ring.length >= 2 && samePoint(ring[0], ring[ring.length - 1])
}

export function closeRing (ring) {
  return ringIsClosed(ring) ? ring : [...ring, ring[0]]
}

// EU rounding to 6 decimals collapses sub-0.1m vertex spacing into duplicates it rejects; preserves closure.
export function dedupeConsecutive (ring) {
  if (ring.length === 0) return { ring, removed: 0 }
  const out = [ring[0]]
  for (let i = 1; i < ring.length; i++) {
    if (!samePoint(ring[i], out[out.length - 1])) out.push(ring[i])
  }
  const deduped = closeRing(out)
  const removed = ring.length - deduped.length + (ringIsClosed(ring) ? 0 : 1)
  return { ring: deduped, removed: Math.max(removed, 0) }
}

// A usable closed ring needs at least 3 distinct vertices (4 points with the closure).
export function ringIsDegenerate (ring) {
  return ring.length < 4
}

// Planar shoelace area (squared degrees); only used to detect collinear rings, so the unit doesn't matter.
export function ringIsZeroArea (ring) {
  const open = ringIsClosed(ring) ? ring.slice(0, -1) : ring
  let doubled = 0
  for (let i = 0; i < open.length; i++) {
    const [x1, y1] = open[i]
    const [x2, y2] = open[(i + 1) % open.length]
    doubled += x1 * y2 - x2 * y1
  }
  return Math.abs(doubled) < 1e-12
}

// A usable WGS84 position: finite numbers within ±180 longitude / ±90 latitude (EU error #11 bounds).
export function positionIsValid (lon, lat) {
  return Number.isFinite(lon) && Number.isFinite(lat) && Math.abs(lon) <= 180 && Math.abs(lat) <= 90
}

// 'malformed' beats 'range'; Point coordinates must be a single position, not an array of them (EU error #14).
export function checkCoordinates (geometry) {
  if (geometry.type === 'Point' && Array.isArray(geometry.coordinates?.[0])) return 'malformed'

  let issue = null
  const walk = (node) => {
    if (issue === 'malformed' || !Array.isArray(node)) return
    if (typeof node[0] === 'number') {
      const [lon, lat] = node
      if (node.length < 2 || !Number.isFinite(lon) || !Number.isFinite(lat)) issue = 'malformed'
      else if (Math.abs(lon) > 180 || Math.abs(lat) > 90) issue = issue ?? 'range'
      return
    }
    for (const child of node) {
      if (Array.isArray(child)) walk(child)
      else { issue = 'malformed'; return }
    }
  }
  walk(geometry.coordinates ?? [])
  return (geometry.coordinates ?? []).length === 0 ? 'malformed' : issue
}

function orientation (p, q, r) {
  const v = (q[1] - p[1]) * (r[0] - q[0]) - (q[0] - p[0]) * (r[1] - q[1])
  if (v > 0) return 1
  if (v < 0) return 2
  return 0
}

function onSegment (p, q, r) {
  return Math.min(p[0], r[0]) <= q[0] && q[0] <= Math.max(p[0], r[0]) &&
    Math.min(p[1], r[1]) <= q[1] && q[1] <= Math.max(p[1], r[1])
}

function segmentsCross (p1, p2, p3, p4) {
  const o1 = orientation(p1, p2, p3)
  const o2 = orientation(p1, p2, p4)
  const o3 = orientation(p3, p4, p1)
  const o4 = orientation(p3, p4, p2)
  if (o1 !== o2 && o3 !== o4) return true
  // Collinear overlap also reads as an invalid crossing to the EU validator.
  if (o1 === 0 && onSegment(p1, p3, p2)) return true
  if (o2 === 0 && onSegment(p1, p4, p2)) return true
  if (o3 === 0 && onSegment(p3, p1, p4)) return true
  if (o4 === 0 && onSegment(p3, p2, p4)) return true
  return false
}

// O(n²) proper-crossing check; fine for parcel-scale rings, and the finalize budget caps rings at 500 vertices.
export function ringSelfIntersects (ring) {
  const open = ringIsClosed(ring) ? ring.slice(0, -1) : ring
  const n = open.length
  if (n < 4) return false
  for (let i = 0; i < n; i++) {
    for (let j = i + 2; j < n; j++) {
      if (i === 0 && j === n - 1) continue // adjacent through the closure point
      if (segmentsCross(open[i], open[(i + 1) % n], open[j], open[(j + 1) % n])) return true
    }
  }
  return false
}

function ringBounds (ring) {
  let minX = Infinity; let minY = Infinity; let maxX = -Infinity; let maxY = -Infinity
  for (const [x, y] of ring) {
    if (x < minX) minX = x
    if (x > maxX) maxX = x
    if (y < minY) minY = y
    if (y > maxY) maxY = y
  }
  return [minX, minY, maxX, maxY]
}

function boundsIntersect (a, b) {
  return a[0] <= b[2] && b[0] <= a[2] && a[1] <= b[3] && b[1] <= a[3]
}

function ringsTouch (ringA, ringB) {
  const a = closeRing(ringA)
  const b = closeRing(ringB)
  for (let i = 0; i < a.length - 1; i++) {
    for (let j = 0; j < b.length - 1; j++) {
      if (segmentsCross(a[i], a[i + 1], b[j], b[j + 1])) return true
    }
  }
  return false
}

// 0.01 ha. Osapiens rejects any polygon or multipart component this size or smaller; at this scale they are
// boundary artifacts, not harvest areas. Shared with the hole splitter's and cut tool's sliver filters.
export const MIN_USABLE_PART_AREA_M2 = 100

// Planar shoelace area scaled to m² at the ring's latitude; plenty accurate at parcel scale.
export function ringAreaSquareMeters (ring) {
  const closed = closeRing(ring ?? [])
  let doubled = 0
  for (let i = 0; i < closed.length - 1; i++) {
    doubled += closed[i][0] * closed[i + 1][1] - closed[i + 1][0] * closed[i][1]
  }
  const metersPerDegreeLon = METERS_PER_DEGREE * Math.cos(((closed[0]?.[1] ?? 0) * Math.PI) / 180)
  return Math.abs(doubled / 2) * METERS_PER_DEGREE * metersPerDegreeLon
}

export function ringIsUndersized (ring) {
  return ringAreaSquareMeters(ring) <= MIN_USABLE_PART_AREA_M2
}

// Part indexes the EU submission file can't keep: outer rings that collapse to fewer than 3 distinct
// vertices or zero area, plus any part at or below MIN_USABLE_PART_AREA_M2 — standalone or a sliver riding
// along a real shape.
export function unusablePartIndexes (geometry) {
  return memoized(geometry, 'unusableParts', () => computeUnusablePartIndexes(geometry))
}

function computeUnusablePartIndexes (geometry) {
  const parts = geometry?.type === 'Polygon' ? [geometry.coordinates]
    : geometry?.type === 'MultiPolygon' ? (geometry.coordinates ?? []) : []
  const indexes = []
  parts.forEach((polygon, index) => {
    const { ring } = dedupeConsecutive(polygon?.[0] ?? [])
    if (ringIsDegenerate(ring) || ringIsZeroArea(ring)) indexes.push(index)
    else if (ringIsUndersized(ring)) indexes.push(index)
  })
  return indexes
}

// True when any two parts of a MultiPolygon meet: shared edge, shared vertex, vertex-on-edge, or one
// part inside the other. segmentsCross already reads endpoint contact and collinear overlap as crossings,
// which matches the EU validator's notion of a self-intersecting polygon. Typical origin of touching
// parts: hole-split halves sharing the cut line, or clip slivers grazing the piece they were cut from.
export function multiPolygonPartsTouch (geometry) {
  if (geometry?.type !== 'MultiPolygon') return false
  const parts = (geometry.coordinates ?? []).filter(polygon => (polygon?.[0]?.length ?? 0) >= 4)
  const bounds = parts.map(polygon => ringBounds(polygon[0]))
  for (let a = 0; a < parts.length; a++) {
    for (let b = a + 1; b < parts.length; b++) {
      if (!boundsIntersect(bounds[a], bounds[b])) continue
      // Every ring of one part against every ring of the other: a part nested in another part's hole
      // can touch the hole ring without ever meeting the outer ring.
      if (parts[a].some(ringA => parts[b].some(ringB => ringsTouch(ringA, ringB)))) return true
      // No boundary contact, but one part wholly inside the other (e.g. duplicated geometry) still overlaps.
      if (pointInGeometry(parts[b][0][0][0], parts[b][0][0][1], { type: 'Polygon', coordinates: parts[a] }) ||
        pointInGeometry(parts[a][0][0][0], parts[a][0][0][1], { type: 'Polygon', coordinates: parts[b] })) return true
    }
  }
  return false
}

// Ramer–Douglas–Peucker on a closed ring; the shared first/last point anchors the recursion so closure holds.
export function simplifyRing (ring, epsilon) {
  const closed = closeRing(ring)
  if (closed.length <= 5) return closed
  const keep = new Array(closed.length).fill(false)
  keep[0] = true
  keep[closed.length - 1] = true
  const stack = [[0, closed.length - 1]]
  while (stack.length) {
    const [first, last] = stack.pop()
    let maxDistance = 0
    let index = -1
    for (let i = first + 1; i < last; i++) {
      const d = perpendicularDistance(closed[i], closed[first], closed[last])
      if (d > maxDistance) {
        maxDistance = d
        index = i
      }
    }
    if (index !== -1 && maxDistance > epsilon) {
      keep[index] = true
      stack.push([first, index], [index, last])
    }
  }
  const out = closed.filter((_, i) => keep[i])
  return out.length >= 4 ? out : closed
}

// Non-mutating. Budget is approached, not guaranteed: rings that would degenerate or self-intersect keep their points.
export function reduceGeometryVertices (geometry, maxVertices, baseEpsilon = 0.000005, maxPasses = 16) {
  if (geometry?.type !== 'Polygon' && geometry?.type !== 'MultiPolygon') return geometry

  const safeSimplify = (ring, epsilon) => {
    const simplified = dedupeConsecutive(simplifyRing(ring, epsilon)).ring
    return ringIsDegenerate(simplified) || ringSelfIntersects(simplified) ? ring : simplified
  }
  const mapRings = (g, fn) => g.type === 'Polygon'
    ? { type: 'Polygon', coordinates: g.coordinates.map(fn) }
    : { type: 'MultiPolygon', coordinates: g.coordinates.map(polygon => polygon.map(fn)) }

  let best = geometry
  let epsilon = baseEpsilon
  for (let pass = 0; pass < maxPasses && countFeatureVertices({ geometry: best }) > maxVertices; pass++, epsilon *= 2) {
    best = mapRings(best, ring => safeSimplify(ring, epsilon))
  }
  return best
}

function perpendicularDistance (point, lineStart, lineEnd) {
  const dx = lineEnd[0] - lineStart[0]
  const dy = lineEnd[1] - lineStart[1]
  const lengthSquared = dx * dx + dy * dy
  if (lengthSquared === 0) {
    return Math.hypot(point[0] - lineStart[0], point[1] - lineStart[1])
  }
  const t = ((point[0] - lineStart[0]) * dx + (point[1] - lineStart[1]) * dy) / lengthSquared
  const projX = lineStart[0] + t * dx
  const projY = lineStart[1] + t * dy
  return Math.hypot(point[0] - projX, point[1] - projY)
}

// Not part of GeometryWarning (analyzeFeature is per-feature and can't see the collection); non-blocking.
export const POINT_COVERED_WARNING = 'pointCovered'

// Even-odd ray cast across every ring of every polygon, so a point inside a hole counts as outside.
export function pointInGeometry (lon, lat, geometry) {
  const polygons = geometry?.type === 'Polygon' ? [geometry.coordinates]
    : geometry?.type === 'MultiPolygon' ? geometry.coordinates : []
  for (const polygon of polygons) {
    let inside = false
    for (const ring of polygon ?? []) {
      for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        const [xi, yi] = ring[i]
        const [xj, yj] = ring[j]
        if ((yi > lat) !== (yj > lat) && lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) inside = !inside
      }
    }
    if (inside) return true
  }
  return false
}

const METERS_PER_DEGREE = 111320 // meridian arc length per degree; longitude scales by cos(lat)

// Mirrors the function app's GeoMath.DistanceToGeometryMeters so both ends agree on nearest-shape ordering.
export function distanceToGeometryMeters (lon, lat, geometry) {
  const cosLat = Math.cos(lat * Math.PI / 180)
  let best = null
  for (const ring of outerRings(geometry)) {
    if (!ring?.length) continue
    // Wraps (j trails i) so an unclosed ring's closing segment still counts, instead of measuring too far.
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const [aLon, aLat] = ring[j]
      const [bLon, bLat] = ring[i]
      const ax = (aLon - lon) * cosLat * METERS_PER_DEGREE
      const ay = (aLat - lat) * METERS_PER_DEGREE
      const bx = (bLon - lon) * cosLat * METERS_PER_DEGREE
      const by = (bLat - lat) * METERS_PER_DEGREE
      const dx = bx - ax
      const dy = by - ay
      const lengthSquared = dx * dx + dy * dy
      const t = lengthSquared === 0 ? 0 : Math.min(1, Math.max(0, -(ax * dx + ay * dy) / lengthSquared))
      const d = Math.hypot(ax + t * dx, ay + t * dy)
      if (best === null || d < best) best = d
    }
  }
  return best
}

// Bbox prefiltering: at composite scale the naive scans froze the review dialog for ~a second on every edit.
export function createPointReconciler (polygonalFeatures) {
  const entries = polygonalFeatures
    .map(feature => ({ feature, bbox: geometryBbox(feature.geometry) }))
    .filter(entry => entry.bbox)

  const contains = (b, lon, lat) => lon >= b[0] && lon <= b[2] && lat >= b[1] && lat <= b[3]
  // Meters from the point to the bbox — a lower bound on the distance to anything inside it.
  const bboxDistance = (b, lon, lat) => {
    const cosLat = Math.cos(lat * Math.PI / 180)
    const dLon = lon < b[0] ? b[0] - lon : lon > b[2] ? lon - b[2] : 0
    const dLat = lat < b[1] ? b[1] - lat : lat > b[3] ? lat - b[3] : 0
    return Math.hypot(dLon * cosLat * METERS_PER_DEGREE, dLat * METERS_PER_DEGREE)
  }

  return {
    // First containing polygon in feature-array order — same rule the daemon's ReconcilePoints stamps by.
    captorFor (lon, lat) {
      const entry = entries.find(e => contains(e.bbox, lon, lat) && pointInGeometry(lon, lat, e.feature.geometry))
      return entry?.feature ?? null
    },

    // The nearest polygon (by outer-ring distance) and how far it is, or null when there are none.
    nearestTo (lon, lat) {
      const candidates = entries
        .map(entry => ({ entry, lowerBound: bboxDistance(entry.bbox, lon, lat) }))
        .sort((a, b) => a.lowerBound - b.lowerBound)
      let bestDistance = null
      let bestFeature = null
      for (const { entry, lowerBound } of candidates) {
        if (bestDistance != null && lowerBound >= bestDistance) break
        const d = distanceToGeometryMeters(lon, lat, entry.feature.geometry)
        if (d != null && (bestDistance == null || d < bestDistance)) {
          bestDistance = d
          bestFeature = entry.feature
        }
      }
      return bestFeature ? { feature: bestFeature, distanceMeters: bestDistance } : null
    }
  }
}

// [minLon, minLat, maxLon, maxLat] over every ring of a Polygon/MultiPolygon; null when unusable.
function geometryBbox (geometry) {
  const polygons = geometry?.type === 'Polygon' ? [geometry.coordinates]
    : geometry?.type === 'MultiPolygon' ? geometry.coordinates : []
  let minLon = Infinity; let minLat = Infinity; let maxLon = -Infinity; let maxLat = -Infinity
  for (const polygon of polygons) {
    for (const ring of polygon ?? []) {
      for (const position of ring ?? []) {
        const [lon, lat] = position
        if (!Number.isFinite(lon) || !Number.isFinite(lat)) continue
        if (lon < minLon) minLon = lon
        if (lon > maxLon) maxLon = lon
        if (lat < minLat) minLat = lat
        if (lat > maxLat) maxLat = lat
      }
    }
  }
  return minLon <= maxLon ? [minLon, minLat, maxLon, maxLat] : null
}

// A regular n-gon covering areaHa hectares, for CSV point-mode tracts the reviewer cannot match to a parcel.
export function bufferPolygonAroundPoint (lon, lat, areaHa, segments = 16) {
  // Circumradius sized so the n-gon itself (not its circumscribed circle) covers areaHa.
  const radius = Math.sqrt((2 * areaHa * 10000) / (segments * Math.sin((2 * Math.PI) / segments)))
  const cosLat = Math.cos(lat * Math.PI / 180)
  const ring = []
  for (let i = 0; i < segments; i++) {
    const angle = (2 * Math.PI * i) / segments
    ring.push([
      toSixDecimalPrecision(lon + (radius * Math.cos(angle)) / (METERS_PER_DEGREE * cosLat)),
      toSixDecimalPrecision(lat + (radius * Math.sin(angle)) / METERS_PER_DEGREE)
    ])
  }
  ring.push([...ring[0]])
  return { type: 'Polygon', coordinates: [ring] }
}

// JSON numbers drop trailing zeros, so a coordinate whose sixth decimal rounds to zero would serialize
// with fewer than the six decimal places EU submissions require. Nudge those ~0.1m toward zero (away
// from the ±90/±180 bounds) so every drawn-area vertex keeps all six decimals.
function toSixDecimalPrecision (value) {
  const rounded = Number(value.toFixed(6))
  if (Math.round(Math.abs(rounded) * 1e6) % 10 !== 0) return rounded
  const nudge = rounded > 0 ? -0.000001 : 0.000001
  return Number((rounded + nudge).toFixed(6))
}

// The outer rings of a geometry: [ring] for Polygon, one per polygon for MultiPolygon, [] for anything else.
function outerRings (geometry) {
  if (geometry?.type === 'Polygon') return geometry.coordinates?.length ? [geometry.coordinates[0]] : []
  if (geometry?.type === 'MultiPolygon') return (geometry.coordinates ?? []).map(polygon => polygon[0]).filter(Boolean)
  return []
}

function interiorRingCount (geometry) {
  if (geometry?.type === 'Polygon') return Math.max((geometry.coordinates?.length ?? 0) - 1, 0)
  if (geometry?.type === 'MultiPolygon') {
    return (geometry.coordinates ?? []).reduce((sum, polygon) => sum + Math.max(polygon.length - 1, 0), 0)
  }
  return 0
}

export function countFeatureVertices (feature) {
  return memoized(feature?.geometry, 'vertexCount', () => countGeometryVertices(feature?.geometry))
}

// Polygons in a MultiPolygon (1 for a Polygon). Memoized so a computed over reactive data never has to read coordinates.
export function countFeatureParts (feature) {
  return memoized(feature?.geometry, 'partCount', () => {
    const geometry = feature?.geometry
    if (geometry?.type === 'Polygon') return 1
    if (geometry?.type === 'MultiPolygon') return (geometry.coordinates ?? []).length
    return 0
  })
}

function countGeometryVertices (geometry) {
  if (geometry?.type === 'Polygon') return (geometry.coordinates ?? []).reduce((sum, ring) => sum + ring.length, 0)
  if (geometry?.type === 'MultiPolygon') {
    return (geometry.coordinates ?? []).reduce((sum, polygon) => sum + polygon.reduce((s, ring) => s + ring.length, 0), 0)
  }
  if (geometry?.type === 'MultiPoint') return (geometry.coordinates ?? []).length
  return geometry?.type === 'Point' ? 1 : 0
}

// Non-mutating analysis of one feature against the EU rules (spec §6 common-errors list).
export function analyzeFeature (feature) {
  const warnings = propertyWarnings(feature?.properties ?? {})
  const geometry = feature?.geometry
  for (const code of memoized(geometry, 'warnings', () => geometryWarnings(geometry))) warnings.add(code)
  return [...warnings]
}

// Property-level rules apply regardless of geometry.
function propertyWarnings (props) {
  const warnings = new Set()
  for (const key of Object.keys(props)) {
    const euName = EU_PROPERTY_NAMES.find(n => n.toLowerCase() === key.toLowerCase())
    if (euName && !ACCEPTED_PROPERTY_SPELLINGS.has(key)) warnings.add(GeometryWarning.PropertyCase)
  }
  const area = props.area ?? props.Area
  if (area != null && typeof area !== 'number') warnings.add(GeometryWarning.AreaNotNumeric)
  const country = props.producerCountry ?? props.ProducerCountry
  if (country != null && !/^[A-Z]{2}$/.test(String(country))) warnings.add(GeometryWarning.InvalidProducerCountry)
  return warnings
}

function geometryWarnings (geometry) {
  if (!geometry) return [GeometryWarning.Degenerate]
  if (!ACCEPTED_GEOMETRY_TYPES.includes(geometry.type)) return [GeometryWarning.InvalidGeometryType]

  const coordinateIssue = checkCoordinates(geometry)
  if (coordinateIssue === 'malformed') return [GeometryWarning.MalformedCoordinates]
  const warnings = new Set()
  if (coordinateIssue === 'range') warnings.add(GeometryWarning.CoordinateRange)

  if (interiorRingCount(geometry) > 0) warnings.add(GeometryWarning.HasHoles)
  for (const ring of outerRings(geometry)) {
    if (!ringIsClosed(ring)) warnings.add(GeometryWarning.OpenRing)
    const { ring: deduped, removed } = dedupeConsecutive(ring)
    if (removed > 0) warnings.add(GeometryWarning.DuplicatePoints)
    if (ringIsDegenerate(deduped)) warnings.add(GeometryWarning.Degenerate)
    else if (ringIsZeroArea(deduped)) warnings.add(GeometryWarning.ZeroArea)
    else if (ringSelfIntersects(deduped)) warnings.add(GeometryWarning.SelfIntersecting)
    if (!ringIsDegenerate(deduped) && !ringIsZeroArea(deduped) && ringIsUndersized(deduped)) {
      warnings.add(GeometryWarning.UndersizedArea)
    }
  }
  if (multiPolygonPartsTouch(geometry)) warnings.add(GeometryWarning.TouchingParts)
  return [...warnings]
}

// The warnings the sanitizer cannot repair automatically; these block an EU submission.
export const BLOCKING_WARNINGS = [
  GeometryWarning.Degenerate,
  GeometryWarning.SelfIntersecting,
  GeometryWarning.TouchingParts,
  GeometryWarning.InvalidGeometryType,
  GeometryWarning.MalformedCoordinates,
  GeometryWarning.CoordinateRange,
  GeometryWarning.ZeroArea
]

export function analyzeFeatureCollection (featureCollection) {
  const byFeature = new Map()
  const counts = {}
  for (const feature of featureCollection?.features ?? []) {
    const warnings = analyzeFeature(feature)
    if (warnings.length === 0) continue
    const key = feature.properties?.featureId ?? feature
    byFeature.set(key, warnings)
    for (const warning of warnings) counts[warning] = (counts[warning] ?? 0) + 1
  }
  return { byFeature, counts }
}
